
import { defineComponent } from 'vue'
import Calendar from 'primevue/calendar'
import Card from 'primevue/card'
import Checkbox from 'primevue/checkbox'
import Dropdown from 'primevue/dropdown'
import InputSwitch from 'primevue/inputswitch'
import knCronDescriptor from './KnCronDescriptor.json'
// import Message from 'primevue/message'
import MultiSelect from 'primevue/multiselect'

export default defineComponent({
    name: 'frequency-card',
    components: {
        Calendar,
        Card,
        Checkbox,
        Dropdown,
        InputSwitch,
        // Message,
        MultiSelect
    },
    props: {
        frequency: {
            type: Object,
            required: true
        }
    },
    emits: ['touched', 'cronValid'],
    data() {
        return {
            knCronDescriptor,
            currentFrequency: {} as any,
            startDate: null as Date | null,
            endDate: null as Date | null,
            startTime: null as Date | null,
            endTime: null as Date | null,
            repeatInterval: null as String | null,
            parameter: null as String | null,
            parameterOptions: [] as any[],
            simpleMonth: true,
            simpleDay: true,
            parameterMonth: null,
            parameterDay: null as Number | null,
            dayOptions: [] as any[],
            selectedDays: [] as any,
            selectedMonths: [] as any,
            simpleDayParameter: null as Number | null
        }
    },
    computed: {
        validDates() {
            let valid = true
            const startDate = this.currentFrequency.startDate
            const now = new Date()
            const endDate = this.currentFrequency.endDate

            if (endDate && endDate.valueOf() < now.valueOf()) {
                valid = false
            }

            if (endDate && startDate && endDate.valueOf() < startDate.valueOf()) {
                valid = false
            }

            return valid
        },
        validInterval(): boolean {
            const valid = this.repeatInterval ? true : false
            return valid
        },
        combinedValidity() {
            const validDates = this.validDates
            const validInterval = this.validInterval
            const combinedValid = validDates && validInterval
            this.$emit('cronValid', combinedValid)
            return combinedValid
        }
    },
    watch: {
        repeatInterval() {
            switch (this.repeatInterval) {
                case 'minute':
                    this.fillParameterOptions(60)
                    break

                case 'hour':
                    this.fillParameterOptions(24)
                    break
                case 'day':
                    this.fillParameterOptions(31)
                    break
                case 'week':
                    this.selectedDays = []
                    break
                case 'month': {
                    this.fillParameterOptions(12)
                    this.fillDayOptions()
                    this.selectedDays = []
                }
            }
            this.parameter = '1' as any
            this.simpleDayParameter = '1' as any
        },
        simpleMonth(value) {
            if (value) {
                this.fillParameterOptions(12)
                delete this.currentFrequency.cron.parameter.months
                this.parameter = '1' as any
                this.updateCronSimpleMonthRepetition(false)
            } else {
                this.parameterOptions = this.knCronDescriptor.monthOptions
                delete this.currentFrequency.cron.parameter.numRepetition
                this.parameter = null
                this.updateCronAdvancedMonthRepetition(false)
            }
        },
        simpleDay(value) {
            if (value) {
                this.fillDayOptions()
                delete this.currentFrequency.cron.parameter.weeks
                delete this.currentFrequency.cron.parameter.days
                this.updateCronSimpleDayRepetition(false)
            } else {
                this.dayOptions = this.knCronDescriptor.weeklyOptions
                delete this.currentFrequency.cron.parameter.dayRepetition
                this.updateCronAdvancedDayRepetition(false)
            }
        },
        frequency() {
            if (!this.frequency) return
            this.loadFrequency()
        }
    },
    async created() {
        this.loadFrequency()
    },
    methods: {
        loadFrequency() {
            this.currentFrequency = this.frequency as any

            this.startDate = new Date(this.currentFrequency.startDate)
            this.startTime = new Date(this.currentFrequency.startDate)
            this.startDate.setHours(0)
            this.startDate.setMinutes(0)
            this.startDate.setSeconds(0)
            this.startTime.setMilliseconds(0)

            if (this.currentFrequency.endDate) {
                this.endDate = new Date(this.currentFrequency.endDate)
                this.endTime = new Date(this.currentFrequency.endDate)
                this.endDate.setHours(0)
                this.endDate.setMinutes(0)
                this.startDate.setSeconds(0)
                this.startTime.setMilliseconds(0)
            } else {
                this.endDate = null
                this.endTime = null
            }

            this.currentFrequency.startTime = ''
            this.currentFrequency.endTime = ''

            if (!this.currentFrequency.cron) {
                this.currentFrequency.cron = { type: 'minute', parameter: { numRepetition: '1' } }
            }

            this.repeatInterval = this.currentFrequency.cron.type
            switch (this.repeatInterval) {
                case 'minute':
                case 'hour':
                case 'day':
                    this.parameter = ('' + this.currentFrequency.cron.parameter.numRepetition) as any
                    break
                case 'week':
                    this.selectedDays = []
                    this.currentFrequency.cron.parameter.days.forEach((day) => this.selectedDays.push(day))

                    break
                case 'month': {
                    if (this.currentFrequency.cron.parameter.months) {
                        this.selectedMonths = []
                        this.currentFrequency.cron.parameter.months.forEach((month) => {
                            this.selectedMonths.push(month)
                        })
                        this.simpleMonth = false
                    } else {
                        this.parameter = this.currentFrequency.cron.parameter.numRepetition as any
                    }
                    if (this.currentFrequency.cron.parameter.weeks) {
                        this.selectedDays = []
                        this.currentFrequency.cron.parameter.days.forEach((day) => this.selectedDays.push('' + day))
                        this.parameterDay = this.currentFrequency.cron.parameter.weeks
                        this.simpleDay = false
                    } else {
                        this.simpleDayParameter = this.currentFrequency.cron.parameter.dayRepetition
                    }
                }
            }
        },
        fillParameterOptions(number: number) {
            this.parameterOptions = []
            for (let i = 1; i <= number; i++) {
                this.parameterOptions.push({
                    name: i,
                    value: '' + i
                })
            }
        },
        fillDayOptions() {
            this.dayOptions = []
            for (let i = 1; i <= 31; i++) {
                this.dayOptions.push({
                    name: i,
                    value: '' + i
                })
            }
        },
        updateCronInterval() {
            this.currentFrequency.cron ? (this.currentFrequency.cron.type = this.repeatInterval) : (this.currentFrequency.cron = { type: this.repeatInterval })
            switch (this.repeatInterval) {
                case 'minute':
                case 'hour':
                case 'day':
                    this.currentFrequency.cron = { type: this.currentFrequency.cron.type, parameter: { numRepetition: this.parameter } }
                    break
                case 'week':
                    this.currentFrequency.cron = { type: this.currentFrequency.cron.type, parameter: { days: this.selectedDays.map((day) => day) } }
                    break
                case 'month': {
                    this.currentFrequency.cron = { type: this.currentFrequency.cron.type, parameter: { numRepetition: this.parameter, dayRepetition: this.simpleDayParameter } }
                }
            }
            this.$emit('touched')
        },
        updateCronNumberOfRepetition() {
            this.currentFrequency.cron = { type: this.currentFrequency.cron.type, parameter: { numRepetition: this.parameter } }
            this.$emit('touched')
        },
        updateCronDays() {
            setTimeout(() => {
                this.currentFrequency.cron = { type: this.currentFrequency.cron.type, parameter: { days: this.selectedDays } }
                this.$emit('touched')
            }, 50)
        },
        updateCronSimpleMonthRepetition(touched: boolean) {
            this.currentFrequency.cron.parameter.numRepetition = this.parameter
            if (touched) {
                this.$emit('touched')
            }
        },
        updateCronSimpleDayRepetition(touched: boolean) {
            this.currentFrequency.cron.parameter.dayRepetition = this.simpleDayParameter
            if (touched) {
                this.$emit('touched')
            }
        },
        updateCronAdvancedMonthRepetition(touched: boolean) {
            this.currentFrequency.cron.parameter.months = this.selectedMonths
            if (touched) {
                this.$emit('touched')
            }
        },
        updateCronAdvancedDayRepetition(touched: boolean) {
            this.currentFrequency.cron.parameter.weeks = this.parameterDay
            this.currentFrequency.cron.parameter.days = this.selectedDays
            if (touched) {
                this.$emit('touched')
            }
        },
        setDate(type: string) {
            this.startDate?.setSeconds(0)
            this.startDate?.setMilliseconds(0)
            const date = type === 'startDate' ? this.startDate?.valueOf() : this.endDate?.valueOf()
            const tempTime = type === 'startDate' ? this.startTime : this.endTime

            let time = 0
            if (tempTime && tempTime instanceof Date) {
                time = tempTime.getHours() * 60 * 60 * 1000 + tempTime.getMinutes() * 60 * 1000
            }

            if (date) {
                this.currentFrequency[type] = date + time
            }

            this.$emit('touched')
        },
        clearEndDate() {
            this.currentFrequency.endDate = null
        }
    }
})
